import { useState, useEffect, useRef } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  House,
  UsersThree,
  TreeStructure,
  ChartLineUp,
  WarningCircle,
  UploadSimple,
  Sparkle,
  Calendar,
  Gift,
  Target,
  ClockCounterClockwise,
  UserGear,
  SignOut,
  Eye,
  EyeSlash,
  List,
  Globe,
} from "@phosphor-icons/react";
import { useSnapshot } from "../contexts/SnapshotContext";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import { Toaster } from "../components/ui/sonner";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "../components/ui/sheet";

function fmtUploadedAt(s) {
  if (!s) return "—";
  const dt = s.uploaded_at ? new Date(s.uploaded_at) : null;
  if (!dt) return s.snapshot_date || s.period || "—";
  return dt.toLocaleString("es-ES", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
    timeZone: "Europe/Madrid",
  });
}

function PeriodPicker({ snapshots, selectedId, setSelectedId, dropUp = true }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const current = snapshots.find((s) => s.id === selectedId) || snapshots[0] || null;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left border border-[#E5E0D8] rounded-lg px-2.5 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-[#C57B67] hover:border-[#C57B67] transition-colors"
        data-testid="period-selector"
      >
        <p className="text-xs font-semibold text-[#2D2A26] leading-tight truncate">
          {current ? current.period : "—"}
          {!selectedId && <span className="ml-1 text-[#C57B67] text-[10px]">(último)</span>}
          {current?.label && <span className="text-[#736F68]"> · {current.label}</span>}
        </p>
        <p className="text-[10px] text-[#736F68] mt-0.5">{fmtUploadedAt(current)}</p>
      </button>

      {open && (
        <div className={`absolute ${dropUp ? "bottom-full mb-1" : "top-full mt-1"} left-0 right-0 bg-white border border-[#E5E0D8] rounded-xl shadow-lg z-50 max-h-64 overflow-y-auto`}>
          <button
            onClick={() => { setSelectedId(null); setOpen(false); }}
            className={`w-full text-left px-3 py-2.5 hover:bg-[#F9F8F6] transition-colors border-b border-[#E5E0D8] ${!selectedId ? "bg-[#F2EFEA]" : ""}`}
          >
            <p className="text-xs font-semibold text-[#2D2A26] leading-tight">
              {snapshots[0]?.period || "—"}
              <span className="ml-1 text-[#C57B67] text-[10px]">(último)</span>
              {snapshots[0]?.label && <span className="text-[#736F68]"> · {snapshots[0].label}</span>}
            </p>
            <p className="text-[10px] text-[#736F68] mt-0.5">{fmtUploadedAt(snapshots[0])}</p>
          </button>
          {snapshots.map((s) => (
            <button
              key={s.id}
              onClick={() => { setSelectedId(s.id); setOpen(false); }}
              className={`w-full text-left px-3 py-2.5 hover:bg-[#F9F8F6] transition-colors border-b border-[#E5E0D8] last:border-0 ${selectedId === s.id ? "bg-[#F2EFEA]" : ""}`}
            >
              <p className="text-xs font-semibold text-[#2D2A26] leading-tight">
                {s.period}
                {s.label && <span className="text-[#736F68] font-normal"> · {s.label}</span>}
              </p>
              <p className="text-[10px] text-[#736F68] mt-0.5">{fmtUploadedAt(s)}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const navigate = useNavigate();
  const {
    snapshots,
    selectedId,
    setSelectedId,
    selected,
    selectedCoordinator,
    setSelectedCoordinator,
    coordinators,
    isGlobal,
    selectedPais,
    setSelectedPais,
    isAdmin,
  } = useSnapshot();
  const { user, isSuperadmin, isMarketing, logout } = useAuth();
  const { t } = useLang();
  const snapshot = selected;

  const [demoMode, setDemoMode] = useState(() => {
    return localStorage.getItem("hesttya_demo_mode") === "true";
  });

  useEffect(() => {
    if (demoMode) {
      document.body.classList.add("demo-mode");
    } else {
      document.body.classList.remove("demo-mode");
    }
  }, [demoMode]);

  const toggleDemoMode = () => {
    setDemoMode((prev) => {
      const next = !prev;
      localStorage.setItem("hesttya_demo_mode", String(next));
      return next;
    });
  };

  const MARKETING_NAV = [
    { to: "/ventajas", label: t("nav_ventajas"), icon: Gift, testid: "nav-ventajas" },
  ];

  const BASE_NAV = [
    { to: "/", label: t("nav_panel"), icon: House, end: true, testid: "nav-dashboard" },
    { to: "/cierre-mes", label: t("nav_cierre"), icon: Calendar, testid: "nav-cierre" },
    { to: "/ventajas", label: t("nav_ventajas"), icon: Gift, testid: "nav-ventajas" },
    { to: "/objetivos", label: t("nav_objetivos"), icon: Target, testid: "nav-objetivos" },
    { to: "/consultoras", label: t("nav_consultoras"), icon: UsersThree, testid: "nav-consultores" },
    { to: "/jerarquia", label: t("nav_jerarquia"), icon: TreeStructure, testid: "nav-jerarquia" },
    { to: "/historico", label: t("nav_historico"), icon: ChartLineUp, testid: "nav-historico" },
    { to: "/alertas", label: t("nav_alertas"), icon: WarningCircle, testid: "nav-alertas" },
    { to: "/subir", label: t("nav_subir"), icon: UploadSimple, testid: "nav-subir" },
  ];

  let NAV;
  if (isMarketing) {
    NAV = MARKETING_NAV;
  } else if (isSuperadmin || isAdmin) {
    NAV = [
      ...BASE_NAV.filter((item) => item.to !== "/subir"),
      { to: "/usuarios", label: t("nav_usuarios"), icon: UserGear, testid: "nav-usuarios" },
    ];
  } else {
    NAV = BASE_NAV;
  }

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex" data-testid="app-layout">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-white border-r border-[#E5E0D8] sticky top-0 h-screen">
        <div className="px-6 pt-7 pb-6 border-b border-[#E5E0D8]">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-[#C57B67] flex items-center justify-center">
              <Sparkle size={18} weight="fill" className="text-white" />
            </div>
            <div>
              <p className="text-[15px] font-semibold text-[#2D2A26] font-display leading-tight">
                Hesttya
              </p>
              <p className="text-[11px] text-[#736F68] tracking-wide uppercase">
                Equipo {snapshot?.coordenador?.split(" ")[0] || "—"}
              </p>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-[#F2EFEA] text-[#2D2A26]"
                    : "text-[#736F68] hover:bg-[#F9F8F6] hover:text-[#2D2A26]"
                }`
              }
            >
              <item.icon size={18} weight="duotone" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="px-4 py-4 border-t border-[#E5E0D8] space-y-2">
          {snapshots.length > 0 || coordinators.length > 0 ? (
            <>
              {isSuperadmin && (
                <div className="space-y-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#736F68] flex items-center gap-1.5">
                    <Globe size={11} weight="bold" />
                    {t("pais_label")}
                  </p>
                  <select
                    value={selectedPais || ""}
                    onChange={(e) => setSelectedPais(e.target.value || null)}
                    className="w-full text-xs border border-[#E5E0D8] rounded-lg px-2.5 py-2 bg-white text-[#2D2A26] focus:outline-none focus:ring-1 focus:ring-[#C57B67]"
                    data-testid="pais-selector"
                  >
                    <option value="">{t("todos_paises")}</option>
                    <option value="es">{t("pais_es")}</option>
                    <option value="pt">{t("pais_pt")}</option>
                  </select>
                </div>
              )}

              {isSuperadmin && coordinators.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#736F68] flex items-center gap-1.5">
                    <UsersThree size={11} weight="bold" />
                    {t("coordinadora")}
                  </p>
                  <select
                    value={selectedCoordinator || ""}
                    onChange={(e) => setSelectedCoordinator(e.target.value)}
                    className="w-full text-xs border border-[#E5E0D8] rounded-lg px-2.5 py-2 bg-white text-[#2D2A26] focus:outline-none focus:ring-1 focus:ring-[#C57B67]"
                    data-testid="coordinator-selector"
                  >
                    <option value="__all__">{t("todos_equipos")}</option>
                    {coordinators
                      .filter((c) => !selectedPais || c.pais === selectedPais)
                      .map((c) => (
                        <option key={c.name} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              {!isGlobal && snapshots.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#736F68] flex items-center gap-1.5">
                    <ClockCounterClockwise size={11} weight="bold" />
                    {t("periodo")}
                  </p>
                  <PeriodPicker
                    snapshots={snapshots}
                    selectedId={selectedId}
                    setSelectedId={setSelectedId}
                  />
                  <p className="text-[10px] text-[#736F68]">{snapshot?.count} {t("consultoras")}</p>
                </div>
              )}
            </>
          ) : (
            <button
              onClick={() => navigate("/subir")}
              className="w-full text-center px-3 py-2 rounded-lg bg-[#C57B67] text-white font-medium hover:bg-[#B06A57] transition-colors text-xs"
              data-testid="empty-state-upload-btn"
            >
              {t("subir_primer_csv")}
            </button>
          )}

          {/* Demo Mode Toggle */}
          <div className="pt-2 border-t border-[#E5E0D8] mt-2">
            <button
              onClick={toggleDemoMode}
              className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors demo-mode-toggle ${
                demoMode
                  ? "bg-[#C57B67]/15 text-[#C57B67] hover:bg-[#C57B67]/25"
                  : "text-[#736F68] hover:text-[#2D2A26] hover:bg-[#F2EFEA]"
              }`}
            >
              {demoMode ? <EyeSlash size={14} weight="fill" /> : <Eye size={14} />}
              <span>{demoMode ? t("modo_demo_activo") : t("modo_demo_ocultar")}</span>
            </button>
          </div>

          {/* User info + logout */}
          <div className="pt-2 border-t border-[#E5E0D8] mt-2">
            <div className="flex items-center justify-between gap-2">
              <Link to="/perfil" className="min-w-0 group flex-1">
                <p className="text-[12px] font-semibold text-[#2D2A26] truncate group-hover:text-[#C57B67] transition-colors">
                  {user?.nombre || user?.email || "—"}
                </p>
                <p className="text-[10px] text-[#736F68] capitalize">{t(`rol_${user?.role}`, user?.role)}</p>
              </Link>
              <button
                onClick={handleLogout}
                title={t("cerrar_sesion")}
                className="p-1.5 text-[#736F68] hover:text-red-600 hover:bg-red-50 rounded-lg transition flex-shrink-0"
              >
                <SignOut size={16} />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-[#E5E0D8]">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#C57B67] flex items-center justify-center">
              <Sparkle size={16} weight="fill" className="text-white" />
            </div>
            <p className="text-base font-semibold text-[#2D2A26] font-display">Hesttya</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleDemoMode}
              title={demoMode ? "Desactivar modo demo" : "Activar modo demo"}
              className={`p-1.5 rounded-lg transition demo-mode-toggle ${
                demoMode ? "text-[#C57B67] bg-[#C57B67]/10" : "text-[#736F68] hover:text-[#2D2A26]"
              }`}
            >
              {demoMode ? <EyeSlash size={18} weight="fill" /> : <Eye size={18} />}
            </button>
            <Sheet>
              <SheetTrigger asChild>
                <button
                  title="Menú"
                  className="p-1.5 text-[#736F68] hover:text-[#2D2A26] hover:bg-[#F2EFEA] rounded-lg transition"
                  data-testid="mobile-menu-btn"
                >
                  <List size={22} weight="bold" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72 flex flex-col bg-white">
                {/* Header */}
                <div className="px-5 pt-6 pb-5 border-b border-[#E5E0D8]">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-xl bg-[#C57B67] flex items-center justify-center">
                      <Sparkle size={18} weight="fill" className="text-white" />
                    </div>
                    <div>
                      <p className="text-[15px] font-semibold text-[#2D2A26] font-display leading-tight">
                        Hesttya
                      </p>
                      <p className="text-[11px] text-[#736F68] tracking-wide uppercase">
                        Equipo {snapshot?.coordenador?.split(" ")[0] || "—"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Nav links */}
                <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
                  {NAV.map((item) => (
                    <SheetClose asChild key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.end}
                        data-testid={item.testid}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                            isActive
                              ? "bg-[#F2EFEA] text-[#2D2A26]"
                              : "text-[#736F68] hover:bg-[#F9F8F6] hover:text-[#2D2A26]"
                          }`
                        }
                      >
                        <item.icon size={18} weight="duotone" />
                        {item.label}
                      </NavLink>
                    </SheetClose>
                  ))}
                </nav>

                {/* Controls: country + coordinator + period + demo + user */}
                <div className="px-4 py-4 border-t border-[#E5E0D8] space-y-3">
                  {snapshots.length > 0 || coordinators.length > 0 ? (
                    <>
                      {isSuperadmin && (
                        <div className="space-y-1">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#736F68] flex items-center gap-1.5">
                            <Globe size={11} weight="bold" />
                            {t("pais_label")}
                          </p>
                          <select
                            value={selectedPais || ""}
                            onChange={(e) => setSelectedPais(e.target.value || null)}
                            className="w-full text-xs border border-[#E5E0D8] rounded-lg px-2.5 py-2 bg-white text-[#2D2A26] focus:outline-none focus:ring-1 focus:ring-[#C57B67]"
                            data-testid="mobile-pais-selector"
                          >
                            <option value="">{t("todos_paises")}</option>
                            <option value="es">{t("pais_es")}</option>
                            <option value="pt">{t("pais_pt")}</option>
                          </select>
                        </div>
                      )}

                      {isSuperadmin && coordinators.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#736F68] flex items-center gap-1.5">
                            <UsersThree size={11} weight="bold" />
                            {t("coordinadora")}
                          </p>
                          <select
                            value={selectedCoordinator || ""}
                            onChange={(e) => setSelectedCoordinator(e.target.value)}
                            className="w-full text-xs border border-[#E5E0D8] rounded-lg px-2.5 py-2 bg-white text-[#2D2A26] focus:outline-none focus:ring-1 focus:ring-[#C57B67]"
                            data-testid="mobile-coordinator-selector"
                          >
                            <option value="__all__">{t("todos_equipos")}</option>
                            {coordinators
                              .filter((c) => !selectedPais || c.pais === selectedPais)
                              .map((c) => (
                                <option key={c.name} value={c.name}>
                                  {c.name}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}

                      {!isGlobal && snapshots.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#736F68] flex items-center gap-1.5">
                            <ClockCounterClockwise size={11} weight="bold" />
                            {t("periodo")}
                          </p>
                          <PeriodPicker
                            snapshots={snapshots}
                            selectedId={selectedId}
                            setSelectedId={setSelectedId}
                            dropUp={false}
                          />
                          <p className="text-[10px] text-[#736F68]">{snapshot?.count} {t("consultoras")}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <SheetClose asChild>
                      <button
                        onClick={() => navigate("/subir")}
                        className="w-full text-center px-3 py-2 rounded-lg bg-[#C57B67] text-white font-medium hover:bg-[#B06A57] transition-colors text-xs"
                      >
                        {t("subir_primer_csv")}
                      </button>
                    </SheetClose>
                  )}

                  {/* Demo mode */}
                  <div className="pt-1 border-t border-[#E5E0D8]">
                    <button
                      onClick={toggleDemoMode}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors demo-mode-toggle ${
                        demoMode
                          ? "bg-[#C57B67]/15 text-[#C57B67] hover:bg-[#C57B67]/25"
                          : "text-[#736F68] hover:text-[#2D2A26] hover:bg-[#F2EFEA]"
                      }`}
                    >
                      {demoMode ? <EyeSlash size={14} weight="fill" /> : <Eye size={14} />}
                      <span>{demoMode ? t("modo_demo_activo") : t("modo_demo_ocultar")}</span>
                    </button>
                  </div>

                  {/* User + logout */}
                  <div className="pt-1 border-t border-[#E5E0D8]">
                    <div className="flex items-center justify-between gap-2">
                      <SheetClose asChild>
                        <Link to="/perfil" className="min-w-0 group flex-1">
                          <p className="text-[12px] font-semibold text-[#2D2A26] truncate group-hover:text-[#C57B67] transition-colors">
                            {user?.nombre || user?.email || "—"}
                          </p>
                          <p className="text-[10px] text-[#736F68] capitalize">{t(`rol_${user?.role}`, user?.role)}</p>
                        </Link>
                      </SheetClose>
                      <button
                        onClick={handleLogout}
                        title={t("cerrar_sesion")}
                        className="p-1.5 text-[#736F68] hover:text-red-600 hover:bg-red-50 rounded-lg transition flex-shrink-0"
                      >
                        <SignOut size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 pt-14 md:pt-0">
        <Outlet />
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}
